import { useState, useMemo } from 'react'

// Utils
import {
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
  endOfYear,
  subYears,
  startOfQuarter,
  endOfQuarter,
  subQuarters,
} from 'date-fns'
import { getAssetTypeLabel } from 'lib/utils/getAssetTypeLabel'
import { getReadableImplantStatus } from 'lib/utils/getReadableImplantStatus'

// Types
import { SelectChangeEvent } from '@mui/material'
import { FilterValues } from 'store/assetsReports/assetsReports.types'
import { FiltersProps, DateRange, UseFiltersLogicReturn } from './Filters.types'

export const dateRanges: DateRange[] = [
  {
    label: 'Custom Range',
    value: 'custom',
    getRange: () => ({ start: new Date(), end: new Date() }),
  },
  {
    label: 'Last Month',
    value: 'lastMonth',
    getRange: () => ({
      start: startOfMonth(subMonths(new Date(), 1)),
      end: endOfMonth(subMonths(new Date(), 1)),
    }),
  },
  {
    label: 'Last Quarter',
    value: 'lastQuarter',
    getRange: () => ({
      start: startOfQuarter(subQuarters(new Date(), 1)),
      end: endOfQuarter(subQuarters(new Date(), 1)),
    }),
  },
  {
    label: 'Last Year',
    value: 'lastYear',
    getRange: () => ({
      start: startOfYear(subYears(new Date(), 1)),
      end: endOfYear(subYears(new Date(), 1)),
    }),
  },
  {
    label: 'Year To Date',
    value: 'ytd',
    getRange: () => ({
      start: startOfYear(new Date()),
      end: new Date(),
    }),
  },
]

const useFiltersLogic = ({
  filters,
  onFiltersChange,
  assets,
  initialExpanded = true,
  onExpandChange,
}: FiltersProps): UseFiltersLogicReturn => {
  const [expanded, setExpanded] = useState(initialExpanded)
  const selectedDateRange = filters?.dateRange || 'custom'

  const activeFiltersCount = Object.entries(filters).reduce(
    (count, [key, value]) => {
      if (key === 'startDate' || key === 'endDate' || key === 'dateRange') {
        return count
      }
      if (key === 'status') {
        return value !== 'Submitted' ? count + 1 : count
      }
      if (Array.isArray(value) && value.length > 0) {
        return count + 1
      }
      return count
    },
    filters.dateRange && filters.dateRange !== 'custom' ? 1 : 0
  )

  const toggleExpanded = () => {
    setExpanded((prev) => !prev)
    onExpandChange?.(!expanded)
  }

  const clearFilter = (key: keyof FilterValues) => {
    onFiltersChange({ [key]: null })
  }

  const handleDateChange = (
    key: 'startDate' | 'endDate',
    date: Date | null
  ) => {
    let updateData: Partial<FilterValues> = { dateRange: 'custom' }

    if (
      key === 'endDate' &&
      date &&
      filters.startDate &&
      date < filters.startDate
    ) {
      updateData = { startDate: date, endDate: date, dateRange: 'custom' }
    } else if (
      key === 'startDate' &&
      date &&
      filters.endDate &&
      date > filters.endDate
    ) {
      updateData = { startDate: date, endDate: date, dateRange: 'custom' }
    } else {
      updateData = { [key]: date, dateRange: 'custom' }
    }

    onFiltersChange(updateData)
  }

  const handleDateRangeChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value
    if (selectedValue === 'custom') {
      return
    }

    const selectedRange = dateRanges.find(
      (range) => range.value === selectedValue
    )
    if (selectedRange) {
      const range = selectedRange.getRange()
      onFiltersChange({
        startDate: range.start,
        endDate: range.end,
        dateRange: selectedValue,
      })
    }
  }

  const handleFilterChange = (
    key: keyof FilterValues,
    values: string[] | null
  ) => {
    onFiltersChange({ [key]: values })
  }

  // Computed values
  const surgeonNames = useMemo(() => {
    return Array.from(
      new Set(
        assets?.map(
          (asset) => `${asset.surgeon?.firstName} ${asset.surgeon?.lastName}`
        )
      )
    )
  }, [assets])

  const manufacturerNames = useMemo(() => {
    return Array.from(new Set(assets?.map((asset) => asset.companyName || '')))
  }, [assets])

  const procedureNames = useMemo(() => {
    return Array.from(
      new Set(
        assets
          .filter((asset) => asset.surgery.procedures.length > 0)
          .map((asset) => asset.surgery.procedures[0]?.description || '')
      )
    )
  }, [assets])

  const locationNames = useMemo(() => {
    return Array.from(
      new Set(
        assets.map((asset) => asset.surgery.visit.location?.department || '')
      )
    )
  }, [assets])

  const assetTypeNames = useMemo(() => {
    return Array.from(
      new Set(
        assets
          .filter((asset) => asset.assets?.[0]?.assetType)
          .map((asset) => getAssetTypeLabel(asset.assets?.[0]?.assetType) || '')
      )
    )
  }, [assets])

  const dispositionNames = useMemo(() => {
    return Array.from(
      new Set(
        assets
          .filter((asset) => asset.assets?.[0]?.implantStatus)
          .map(
            (asset) =>
              getReadableImplantStatus(asset.assets?.[0]?.implantStatus) || ''
          )
      )
    )
  }, [assets])

  const hasActiveFilters =
    activeFiltersCount > 0 ||
    !!filters.startDate ||
    !!filters.endDate ||
    selectedDateRange !== 'custom' ||
    filters.status !== 'Submitted'

  // This will be handled in the component file to avoid circular dependencies
  const chips: JSX.Element[] = []

  return {
    expanded,
    selectedDateRange,
    activeFiltersCount,
    hasActiveFilters,
    chips,
    surgeonNames,
    manufacturerNames,
    procedureNames,
    locationNames,
    assetTypeNames,
    dispositionNames,
    toggleExpanded,
    clearFilter,
    handleDateChange,
    handleDateRangeChange,
    handleFilterChange,
  }
}

export default useFiltersLogic
